/* Created by Interactive 63 - https://interactive63.com */
/**
 * What the inspector shows when nothing is selected: the scene duration, how many elements are
 * animated, and a hint.
 */
import { preferences } from '../../preferences';
import { setDuration } from '../../store/property-actions';
import type { Store } from '../../store/store';
import { formatTime, parseTime } from '../../time-format';
import { h } from '../../ui/dom';

export class SceneSection {
  readonly el: HTMLElement;
  private readonly duration: HTMLInputElement;
  private readonly scale: HTMLInputElement;
  private readonly count: HTMLElement;

  /** Builds the section. */
  constructor(private readonly store: Store) {
    this.duration = h('input', {
      class: 'scene-duration',
      'aria-label': 'Scene duration',
      autocomplete: 'off',
    });
    this.scale = h('input', {
      type: 'checkbox',
      checked: preferences.scaleKeyframes,
      id: 'scale-keyframes',
    });
    this.scale.addEventListener('change', () => {
      preferences.scaleKeyframes = this.scale.checked;
    });
    this.count = h('p', { class: 'scene-count' });

    this.duration.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') this.duration.blur();
      else if (event.key === 'Escape') {
        this.refresh();
        this.duration.blur();
      }
    });
    this.duration.addEventListener('blur', () => {
      const ms = parseTime(this.duration.value);
      if (ms !== null) {
        setDuration(this.store, ms, {
          scale: this.scale.checked,
          confirmRemoval: (lost) =>
            window.confirm(
              `${lost} keyframe${lost === 1 ? ' is' : 's are'} after the new end time and will be removed. Continue?`,
            ),
        });
      }
      this.refresh();
    });

    this.el = h(
      'section',
      { class: 'inspector-section scene-section' },
      h('h3', { class: 'section-title' }, 'Scene'),
      h('label', { class: 'keyframe-field' }, 'Duration', this.duration),
      h('label', { class: 'scale-option' }, this.scale, ' Scale keyframes to fit'),
      this.count,
      h(
        'p',
        { class: 'panel-empty' },
        'Select an element on the stage or in Layers to animate it.',
      ),
    );
    this.refresh();
  }

  /** Updates the duration and animated element count. */
  refresh(): void {
    const { project } = this.store.getState();
    if (document.activeElement !== this.duration)
      this.duration.value = formatTime(project.scene.duration);
    const animated = new Set(project.tracks.map((t) => t.elementId)).size;
    this.count.textContent = `${animated} animated element${animated === 1 ? '' : 's'}`;
  }
}
